/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-12-24 11:08:05
 **/

import { create } from "zustand/index";
import { immer } from "zustand/middleware/immer";
import { createJSONStorage, persist } from "zustand/middleware";
import omit from "lodash-es/omit";
import Network from "../../../frameworks/libraries/network/network";
import { Topics } from "@/frameworks/core/commons";

/**
 * ChatDataState represents the state of a chat UI component or module.
 *
 * This type is designed to store and manage the properties related to chat interactions, chat UI behavior,
 * and user-defined inputs for searching or prioritizing chat conversations.
 *
 * Properties:
 * - fullsize_chat_id: A unique string identifier for the currently selected full-size chat window.
 * - is_click_ignore: A boolean flag to indicate whether a click event is to be disregarded for certain interactions.
 * - chat_search_text: A string containing the current search text input for filtering or finding chats.
 * - chat_search_selected: A string representing the selected chat ID or search result from the search functionality.
 * - priority_space_chat: A boolean flag that defines whether the chat is in a prioritization state.
 * - show_fullsize_chat: A boolean flag to indicate whether the full-size chat panel is currently displayed.
 * - room_ui: A collection of state data where each key corresponds to a chat room's unique identifier
 *   and stores its associated UI state as defined by ChatUI.RoomUIState.
 */
type ChatDataState = {
	fullsize_chat_id: string;
	is_click_ignore: boolean;
	chat_search_text: string;
	chat_search_selected: string;
	totalChat_search_area_open: boolean;
	chat_searching: { [channelId: string]: boolean };

	priority_space_chat: boolean;
	show_fullsize_chat: boolean;
	room_ui: Record<string, ChatUI.RoomUIState>;

	space_room_id: string;
	space_room_name: string;
	space_is_audio_zone: boolean;
	threadID: string | null;
};

/**
 * ChatDataAction represents a set of functions designed to manage and manipulate chat-related data and preferences.
 *
 * Methods:
 * - set_fullsize_chat_id: Sets the ID for full-size chats.
 * - set_chat_search_text: Sets the chat search query text.
 * - set_chat_search_selected: Updates the currently selected chat search result.
 * - toggle: Toggles specified chat-related boolean states or features.
 */
type ChatDataAction = {
	set_fullsize_chat_id: (id: string) => void;
	set_chat_search_text: (text: string) => void;
	set_chat_search_selected: (selected: string) => void;
	set_chat_searching: (channelId: string, value: boolean) => void;
	set_totalChat_search_area_open: (openState: boolean) => void;
	toggle: (key: "prioritySpaceChat" | "showFullSizeChat") => void;
	add_room_ui: (room_id: string) => void;
	remove_room_ui: (room_id: string) => void;
	update_room_ui: (room_id: string, ui: ChatUI.RoomUIState) => void;
	reset_room_ui: (room_id: string) => void;
	change_replying_state: (room_id: string, is_replying: boolean) => void;
	change_updating_state: (room_id: string, is_updating: boolean) => void;
	reset_all_room_ui: () => void;
	change_room: (id: string, name: string, is_audio_zone: boolean) => void;
	exit_space: () => void;
	setThreadID: (id: string) => void;
	clearThreadID: () => void;
};

type ChatDataStore = ChatDataState & ChatDataAction;

const DefaultChatDataState: ChatDataState = {
	fullsize_chat_id: "",
	is_click_ignore: false,
	chat_search_text: "",
	chat_search_selected: "",
	chat_searching: {},
	priority_space_chat: false,
	show_fullsize_chat: false,
	room_ui: {},
	space_room_id: "",
	space_room_name: "",
	space_is_audio_zone: false,
	totalChat_search_area_open: false,
	threadID: null
};

/**
 * useChatDataStore is a Zustand-based state management store for handling chat-related
 * state data. It utilizes Immer for immutable state updates and persists state in localStorage.
 *
 * The store manages various properties such as:
 * - `fullsize_chat_id`: Represents the current chat ID in full size view.
 * - `is_click_ignore`: Tracks whether a click event should be ignored in the chat interface.
 * - `chat_search_text`: Stores the text used in chat search.
 * - `chat_search_selected`: Maintains the currently selected option in the chat search.
 * - `priority_space_chat` and `show_fullsize_chat`: UI-related properties to manage visibility of prioritized chat spaces and full size chat views.
 *
 * Features:
 * - Provides methods (`set_` and `toggle`) to update state properties.
 * - Employs localStorage persistence for maintaining specific state properties across sessions.
 * - The `partialize` option ensures that only selected properties are persisted.
 *
 * Usage of this store ensures centralized management of chat data and related UI states.
 */
export const useChatDataStore = create(
	persist(
		immer<ChatDataStore>((set) => ({
			...DefaultChatDataState,
			set_fullsize_chat_id: (id) =>
				set((state) => {
					state.fullsize_chat_id = id;
				}),
			set_chat_search_text: (text) =>
				set((state) => {
					state.chat_search_text = text;
				}),
			set_chat_search_selected: (selected) =>
				set((state) => {
					state.chat_search_selected = selected;
				}),
			set_chat_searching: (channelId, value) =>
				set((state) => ({
					chat_searching: {
						...state.chat_searching,
						[channelId]: value
					}
				})),
			toggle: (key) =>
				set((state) => {
					const _key = key === "prioritySpaceChat" ? "priority_space_chat" : "show_fullsize_chat";
					state[_key] = !state[_key];
				}),
			add_room_ui: (room_id) =>
				set((state) => {
					state.room_ui[room_id] = { isUpdating: false, isReplying: false };
				}),
			remove_room_ui: (room_id) =>
				set((state) => {
					state.room_ui[room_id] = omit(state.room_ui, room_id);
				}),
			update_room_ui: (room_id, ui) =>
				set((state) => {
					state.room_ui[room_id] = {
						...state.room_ui[room_id],
						...ui
					};
				}),
			reset_room_ui: (room_id) =>
				set((state) => {
					state.room_ui[room_id] = DefaultChatDataState.room_ui[room_id];
				}),
			set_totalChat_search_area_open: (openState) =>
				set((state) => {
					state.totalChat_search_area_open = openState;
				}),
			change_replying_state: (room_id, is_replying) =>
				set((state) => {
					state.room_ui[room_id].isReplying = is_replying;
				}),
			change_updating_state: (room_id, is_updating) =>
				set((state) => {
					state.room_ui[room_id].isUpdating = is_updating;
				}),
			reset_all_room_ui: () =>
				set((state) => {
					state.room_ui = {};
				}),
			change_room: (id: string, name: string, is_audio_zone: boolean) =>
				set((state) => {
					state.space_room_id = id;
					state.space_room_name = name;
					state.space_is_audio_zone = is_audio_zone;
				}),
			exit_space: () =>
				set((state) => {
					try {
						if (state.space_room_id.length > 0) {
							Network.instances["default"].unsubscribe(Topics.Room(state.space_room_id), false);
						}
						state.space_room_id = "";
						state.space_room_name = "";
						state.space_is_audio_zone = false;
					} catch (e) {
						console.error(e);
					}
				}),
			setThreadID: (id: string | null) =>
				set((state) => {
					state.threadID = id;
				}),
			clearThreadID: () =>
				set((state) => {
					state.threadID = null;
				})
		})),
		{
			name: "cds",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				priority_space_chat: state.priority_space_chat,
				show_fullsize_chat: state.show_fullsize_chat
			})
		}
	)
);
